import _ from 'lodash';
import {useContext, useEffect, useState} from "react";
import InlineInputControlRow from "../../ui/layout/InlineInputControlRow";
import LeftRightSplit from "../../ui/LeftRightSplit";
import TextBox from "../../ui/TextBox";
import LabeledControl from "../../ui/LabeledControl";
import ContainerTypeDropDown from "../../uiControls/ContainerTypeDropDown";
import Date from "../../ui/Date";
import Float from "../../ui/Float";
import DateTime from "../../ui/DateTime";
import {useTypedFastStorage} from "../../hooks/useFastStorage";
import SecondaryButton from "../../ui/SecondaryButton";
import {OmContainer} from "../../fms.core/OmContainer";
import useGlobalOkDialog from "../../hooks/useGlobalOkDialog";
import useGlobalYesNoDialog from "../../hooks/useGlobalYesNoDialog";
import { kgToLbs } from '../../util';
import { JobNames } from '../../framework.core/JobNames';
import ApplicationContext from '../../ApplicationContext';
import { round } from 'mathjs';
import { getSQLInfoList } from '../../fms.core/data/TrackingModule';
import { TrackableEntity } from '../../fms.core/data/ChangeTrackable';
import { ApiResult, InsertResultsWithData } from '../../framework.core/data/ApiResult';
import useDirtyCheck from '../../hooks/useDirtyCheck2';

interface FlexTankStorageKeys {
  modelStorage: string,
};

interface FlexTankViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  isView?: boolean,
};

interface FlexTankLoadedDetailsViewParams {
  flexTank: OmContainer,
  onPopupClose?: () => void,
  isView?: boolean,
  isSeparate?: boolean,
  onSave?: (containerId: number, data: OmContainer) => void,
  onDelete?: (containerId: number) => void,
  setFlexTank?:(x: Partial<OmContainer>) => void,
};

function getFlexTankStorageKeys(): FlexTankStorageKeys {
  return {modelStorage: 'print-bill-report'}
}

function FlexTankImpl(params: FlexTankViewParams) {
  const {id, onPopupClose} = params;
  const {modelStorage} = getFlexTankStorageKeys();

  const [inStorage, setInStorage, removeFlexTankFromStorage] = useTypedFastStorage<OmContainer>(modelStorage, null);
  const { setCleanState } = useDirtyCheck(modelStorage);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const appContext = useContext(ApplicationContext);

  useEffect(() => {
      initialize();
      return () => {
        removeFlexTankFromStorage()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    const apiResult = await appContext.api.getJobDetail(JobNames.FlexiTank, {id});
    if(apiResult && apiResult.success && apiResult.result) {
      const data: OmContainer = new OmContainer(apiResult.result);
      return data;
    }
    return {}
  }

  async function initialize() {
    const data = await loadExisting();
    setCleanState(data);
    const entity = new OmContainer(_.cloneDeep(data));
    entity.trackable = true;
    if(!entity?.serialNo){
      entity.serialNo = "TBD";
    }
    setInStorage(entity);
  }

  if (!inStorage) {
    return null;
  }

  async function onDelete(){
    await fsClearFlexiTankData();
    await saveFunc("CLEAR");
  }

  async function onSave(){
    await saveFunc("SAVE");
  }

  async function saveFunc(para: string){

    if(para !== 'CLEAR'){
      if(inStorage?.ftLoadDay === null){
        setInStorage({ftLoadDate : null});
      } else if (inStorage?.ftLoadTime === null){
        setInStorage({ftLoadDate: inStorage?.ftLoadDay});
      } else {
        setInStorage({ftLoadDate: inStorage?.ftLoadDay??"" + inStorage?.ftLoadTime})
      }
      setInStorage({isFlexiTank: true})
    }

    const sqlInfoList = getSQLInfoList(inStorage as TrackableEntity);

    const apiResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.FlexiTank, sqlInfoList);
     if(apiResult.success){
       if(para === 'CLEAR'){
         await showGlobalOkDialog("Successfully Done");
       }
      //   else if (para === 'SAVE'){
      //    await showGlobalOkDialog("Sucessfully Saved");
      //  }
       if(onPopupClose){
         onPopupClose();
       }
    }
  }

  async function fsClearFlexiTankData(){
    setInStorage({
      isFlexiTank: false,
      tankSize:'',
      serialNo:'',
      fitByDate: null,
      commodity: '',
      payload: 0,
      capacity:0,
      puNo: '',
      ftLoadDate: null,
      sealNo2:'',
      sealNo3:'',
      hts:'',
      netKgs: 0,
      cbm: 0,
      gallon: 0,
    })
  }


  return (
    <FlexTankInitializedImpl
      flexTank={inStorage}
      onPopupClose={params.onPopupClose}
      isView={params.isView}
      isSeparate={true}
      setFlexTank={setInStorage}
      onDelete={onDelete}
      onSave={onSave}
    />
  );
}

function createInitialFlexiTankFrom(initial: OmContainer): OmContainer {
  const result = _.cloneDeep(initial);
  if (!result.serialNo) {
    result.serialNo = 'TBD';
  }

  return result;
}

export function FlexTankInitializedImpl({
  flexTank,
  onPopupClose,
  isView,
  isSeparate,
  setFlexTank,
  onSave,
  onDelete,
}: FlexTankLoadedDetailsViewParams) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const [editableFlexTank, setEditableFlexTank] = useState<OmContainer>(createInitialFlexiTankFrom(flexTank));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      const focusElement = document.getElementById('ft-flexTank-no') as HTMLElement;
      if (focusElement) {
        focusElement.focus();
      }
    }, 10)
  },
  []
);

  function onChange(change: Partial<OmContainer>) {
    if(!isSeparate){
      const eft = _.cloneDeep(_.merge(editableFlexTank, change));
      setEditableFlexTank(eft);
    }else{
      if(setFlexTank) setFlexTank(change);
    }
    
    if (!dirty) {
      setDirty(true);
    }
  }

  async function processCancel() {
    if (dirty) {
      const doSave = await showGlobalYesNoDialog('Do you want to save Flexi Tank Information?',false,0,true);
      if (doSave) {
        await processSave();
      }
      else {
        if (onPopupClose) {
          onPopupClose();
        }
      }
    }
    else {
      if (onPopupClose) {
        onPopupClose();
      }
    }
  }

  async function processDelete() {
    if (onDelete) {
      const doDelete = await showGlobalYesNoDialog('Do you want to delete Flexi Tank Information?',false, 0, true, 'warning');
      if (doDelete) {
        onDelete(flexTank.id);
        if (onPopupClose) {
          onPopupClose();
        }
      }
    }
  }

  async function processSave() {
    if (!editableFlexTank.serialNo) {
      showGlobalOkDialog('Need to enter Serial #');
      const focusElement = document.getElementById('ft-serial-no');
      if (focusElement) {
        focusElement.focus();
      }

      return;
    }

    if (onSave) {
      onSave(flexTank.id, editableFlexTank);
    }
  }

  return (
    <div className='report-popup'>
      <div className="report-row">
        <InlineInputControlRow label='Container #' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="90px"
            rightSize="calc(100% - 90px)"
            left={
              <TextBox name='flexTank-detail-containerNo' textId={`ft-flexTank-no`} value={isSeparate ? flexTank.containerNo: editableFlexTank.containerNo} onChange={e => onChange({containerNo: e.target.value})} maxLength={11} disabled={isView} />
            }
            right={
              <LabeledControl
                label="Type"
                labelSize="40px"
                alignRight
                control={
                  <ContainerTypeDropDown
                    maxLength={10}
                    isShort={true}
                    name='flexTank-detail-type'
                    selectId={`ft-type`}
                    value={(isSeparate ? flexTank.conType : editableFlexTank.conType ) || ''}
                    disabled={isView}
                    onChange={conType => {
                      onChange({conType: conType || ''});
                    }}
                    jobName={JobNames.FlexiTank}
                  />
                }
              />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='Tank Size' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="90px"
            rightSize="calc(100% - 90px)"
            left={
              <TextBox name='flexTank-detail-tankSize' textId='ft-tank-size' value={isSeparate ? flexTank.tankSize : editableFlexTank.tankSize} maxLength={20} disabled={isView} onChange={e => onChange({tankSize: e.target.value})}/> }
            right={
              <LabeledControl
                label="Fit by"
                labelSize="40px"
                alignRight
                control={
                  <Date name='flexTank-detail-fitBy' hideIcon dateId='ft-fit-by-date' disabled={isView} value={isSeparate? flexTank.fitByDate : editableFlexTank.fitByDate} onChange={fitByDate => onChange({fitByDate})} />
                }
              />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='Commodity' mode='smaller-2x' smallMargin>
          <TextBox name='flexTank-detail-commodity' textId='ft-commodity' disabled={isView} value={isSeparate? flexTank.commodity : editableFlexTank.commodity} maxLength={100} onChange={e => onChange({commodity: e.target.value})} />
        </InlineInputControlRow>

        <InlineInputControlRow label='Payload (KG)' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <Float name='flexTank-detail-payload' floatId={`ft-payload`} value={isSeparate ? flexTank.payload: editableFlexTank.payload} disabled={isView} onChange={payload => onChange({payload: payload || 0})} min={0} max={99999999} decimals={4} />
            }
            right={
              <LabeledControl
                label="Capacity (G)"
                labelSize="90px"
                alignRight
                control={
                  <Float name='flexTank-detail-capacity' floatId={`ft-capacity`} value={isSeparate ? flexTank.capacity : editableFlexTank.capacity} disabled={isView} onChange={capacity => onChange({capacity: capacity || 0})} min={0} max={99999999} decimals={4} />
                }
              />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='P/U #' mode='smaller-2x' smallMargin>
          <TextBox name='flexTank-detail-pu' textId='ft-pu-no' value={isSeparate ? flexTank.puNo : editableFlexTank.puNo} maxLength={50} disabled={isView} onChange={e => onChange({puNo: e.target.value})}/>
        </InlineInputControlRow>

        <InlineInputControlRow label='Load Date' mode='smaller-2x' smallMargin>
          <div style={{width: 130}}>
            <DateTime  name='flexTank-detail-loadDate' dateId='ft-load-date' timeId='ft-load-date-time' value={isSeparate ? flexTank.ftLoadDate : editableFlexTank.ftLoadDate} onChange={ftLoadDate => onChange({ftLoadDate})} disabled={isView} />
          </div>
        </InlineInputControlRow>

        <InlineInputControlRow label='Seal #1' mode='smaller-2x' smallMargin>
          <div style={{width: 90}}>
            <TextBox name='flexTank-detail-seal1' textId='ft-seal-no' value={isSeparate ? flexTank.sealNo : editableFlexTank.sealNo} maxLength={20} onChange={e => onChange({sealNo: e.target.value})} disabled={isView} />
          </div>
        </InlineInputControlRow>

        <InlineInputControlRow label='Seal #2' mode='smaller-2x' smallMargin>
          <div style={{width: 90}}>
            <TextBox name='flexTank-detail-seal2' textId='ft-seal-no-2' value={isSeparate ? flexTank.sealNo2 : editableFlexTank.sealNo2} maxLength={20} onChange={e => onChange({sealNo2: e.target.value})} disabled={isView} />
          </div>
        </InlineInputControlRow>

        <InlineInputControlRow label='Seal #3' mode='smaller-2x' smallMargin>
          <div style={{width: 90}}>
            <TextBox name='flexTank-detail-seal3' textId='ft-seal-no-3' value={isSeparate ? flexTank.sealNo3 : editableFlexTank.sealNo3} maxLength={20} onChange={e => onChange({sealNo3: e.target.value})} disabled={isView} />
          </div>
        </InlineInputControlRow>

        <InlineInputControlRow label='Serial #' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="90px"
            rightSize="calc(100% - 90px)"
            left={
              <TextBox name='flexTank-detail-serialNo' textId='ft-serial-no' value={isSeparate ? flexTank.serialNo : editableFlexTank.serialNo} maxLength={50} onChange={e => onChange({serialNo: e.target.value})} disabled={isView} />
            }
            right={
              // !isSeparate ? (
                <div style={{paddingLeft: '15px', color: 'red'}}>
                  (*) Required
                </div>
              // ) : <>&nbsp;</>
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='Weight (Net)' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <Float name='flexTank-detail-net' floatId={`ft-net`} value={isSeparate ? flexTank.netKgs : editableFlexTank.netKgs} disabled={isView} onChange={netWeight => onChange({netKgs: netWeight ?? 0})} min={0} max={99999999} decimals={2} />
            }
            right={
              <LabeledControl
                labelSize='90px'
                label='Gross'
                alignRight
                control={
                  <Float name='flexTank-detail-gross' floatId={`ft-gross`} value={isSeparate ? flexTank.gKgs : editableFlexTank.gKgs} disabled={isView} onChange={grossWeight => onChange({gKgs: grossWeight || 0, gLbs: round(kgToLbs(grossWeight) ?? 0 ,2)})} min={0} max={99999999} decimals={2} />
                }
              />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='Weight (CBM)' mode='smaller-2x' smallMargin>
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <Float name='flexTank-detail-cbm' floatId={`ft-cbm`} value={isSeparate ? flexTank.cbm : editableFlexTank.cbm} disabled={isView} onChange={cbm => onChange({cbm: cbm || 0})} min={0} max={99999999} decimals={3} />
            }
            right={
              <LabeledControl
                labelSize='90px'
                label='Gallon'
                alignRight
                control={
                  <Float name='flexTank-detail-gallon' floatId={`ft-gross`} value={isSeparate ? flexTank.gallon : editableFlexTank.gallon} disabled={isView} onChange={gallon => onChange({gallon: gallon || 0})} min={0} max={99999999} decimals={2} />
                }
              />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label='HTS / NCM' mode='smaller-2x' smallMargin>
          <TextBox name='flexTank-detail-hts' textId='ft-hts' value={isSeparate ? flexTank.hts : editableFlexTank.hts} maxLength={20} disabled={isView} onChange={e => onChange({hts: e.target.value})} />
        </InlineInputControlRow>
      </div>
      <div className='clearfix'/>
      <div className='report-footer'>
        {!isView &&
          <SecondaryButton
            size="regular"
            onClick={processSave}
            skipTableCheck
            name='flexTank-detail-save'
          >
            SAVE
          </SecondaryButton>
        }
        {!isView &&
          <SecondaryButton
            size="regular"
            onClick={processDelete}
            skipTableCheck
            name='flexTank-detail-delete'
          >
            DELETE
          </SecondaryButton>
        }
        <SecondaryButton
          size="regular"
          onClick={processCancel}
          skipTableCheck
          name='flexTank-detail-cancel'
        >
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const FlexTankDetails = ({
                           onPopupClose,
                           selectedElementId
                         }: { onPopupClose?: () => void, selectedElementId: number }) => {

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <FlexTankImpl id={selectedElementId.toString()} onPopupClose={onPopupClose} key={`flexTank-details`}/>
  );
};
export default FlexTankDetails;